import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { ChevronLeft, Mail, Clock } from "lucide-react";

async function throwIfNotOk(res: Response) {
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).message || `Request failed (${res.status})`);
  }
  return res;
}

export default function ContactUs() {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      }).then(throwIfNotOk);

      toast({ title: "Message sent", description: "We'll get back to you within 1-2 business days." });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast({
        title: "Failed to send message",
        description: err instanceof Error ? err.message : "Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelClass = "block text-sm font-medium text-slate-300 mb-1.5";
  const inputClass = "bg-[#0D1B2A] border-[#415A77] text-white placeholder:text-slate-500 focus:border-[#E0A458] focus:ring-[#E0A458]/20";

  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-2xl mx-auto px-4 py-12">

        <a href="/" className="inline-flex items-center text-[#E0A458] hover:text-[#f0b467] text-sm mb-8 transition-colors">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Home
        </a>

        <div className="rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-6 py-6 shadow-lg shadow-black/25 mb-8">
          <h1 className="font-montserrat font-bold text-3xl text-white">Contact Us</h1>
          <p className="text-slate-400 text-sm mt-1">Have a question or need help? We're here for you.</p>
        </div>

        <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className={labelClass}>Your Name</label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required className={inputClass} placeholder="Jane Smith" />
            </div>
            <div>
              <label htmlFor="email" className={labelClass}>Email Address</label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder="jane@example.com" />
            </div>
            <div>
              <label htmlFor="subject" className={labelClass}>Subject</label>
              <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required className={inputClass} placeholder="Question about my account" />
            </div>
            <div>
              <label htmlFor="message" className={labelClass}>Message</label>
              <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} className={inputClass} placeholder="Tell us how we can help..." />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold w-full"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 p-5 flex items-start gap-3">
            <Mail className="h-5 w-5 text-[#E0A458] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">Email</p>
              <p className="text-sm text-slate-400">support@mylegacyloop.com</p>
            </div>
          </div>
          <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 p-5 flex items-start gap-3">
            <Clock className="h-5 w-5 text-[#E0A458] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">Response Time</p>
              <p className="text-sm text-slate-400">Within 1-2 business days</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
